import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { congregationService } from '@/services/api';
import { Church, Copy, Check, Plus, ArrowLeft, RefreshCw, Ban, Wallet, Users, AlertTriangle, Banknote } from 'lucide-react';
import { WithdrawModal, WithdrawalsHistory } from '@/components/congregations/WithdrawalPanel';

const fmt = (n) => new Intl.NumberFormat('es-VE', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(Number(n || 0));

export default function Congregations() {
  const navigate = useNavigate();
  const [summary, setSummary] = useState({ total_congregations: 0, total_balance: 0, congregations: [] });
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [form, setForm] = useState({ name: '', code: '' });
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState('');
  const [issuedKey, setIssuedKey] = useState(null); // { api_key, code, format }
  const [copied, setCopied] = useState(null);
  const [withdrawFor, setWithdrawFor] = useState(null);
  const [withdrawalsKey, setWithdrawalsKey] = useState(0);
  const [detailFor, setDetailFor] = useState(null);

  useEffect(() => { load(); }, []);

  const load = async () => {
    try {
      setLoading(true);
      const data = await congregationService.getSummary();
      setSummary(data);
    } catch (e) {
      console.error('Error loading congregations', e);
    } finally {
      setLoading(false);
    }
  };

  const copy = (text, id) => {
    navigator.clipboard.writeText(text);
    setCopied(id);
    setTimeout(() => setCopied(null), 2000);
  };

  const handleCreate = async () => {
    setError('');
    const code = (form.code || '').trim().toUpperCase();
    if (!form.name.trim() || !/^[A-Z0-9]{2,20}$/.test(code)) {
      setError('Nombre requerido y código de 2-20 caracteres (solo MAYÚSCULAS y números).');
      return;
    }
    try {
      setCreating(true);
      const res = await congregationService.create(form.name.trim(), code);
      setShowCreate(false);
      setForm({ name: '', code: '' });
      setIssuedKey({ api_key: res.api_key, code: res.congregation.code, format: res.congregation.order_reference_format });
      load();
    } catch (e) {
      setError(e.response?.data?.detail || 'Error al crear la congregación.');
    } finally {
      setCreating(false);
    }
  };

  const handleRegenerate = async (projectId, name) => {
    if (!window.confirm(`¿Regenerar la API Key de "${name}"? La anterior quedará revocada de inmediato.`)) return;
    try {
      const res = await congregationService.regenerateKey(projectId);
      setIssuedKey({ api_key: res.api_key, code: res.code, format: `CONG-${res.code}-<consecutivo>` });
      load();
    } catch (e) {
      alert(e.response?.data?.detail || 'Error al regenerar la key.');
    }
  };

  const handleRevoke = async (projectId, name) => {
    if (!window.confirm(`¿Revocar la API Key activa de "${name}"? Sus cobros dejarán de funcionar hasta regenerar una nueva.`)) return;
    try {
      await congregationService.revokeKey(projectId);
      load();
    } catch (e) {
      alert(e.response?.data?.detail || 'Error al revocar la key.');
    }
  };

  return (
    <div className="min-h-screen bg-gray-50" data-testid="congregations-page">
      {/* Header */}
      <div className="border-b" style={{ backgroundColor: '#051C2C' }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-4">
            <div className="flex items-center gap-4">
              <button onClick={() => navigate('/dashboard')} className="text-white hover:text-gray-300">
                <ArrowLeft className="w-5 h-5" />
              </button>
              <img src="/logos/logo-fondo-oscuro.png" alt="Caura Pay" className="h-8" />
              <span className="text-xl font-bold text-white border-l border-white/30 pl-4">Congregaciones</span>
            </div>
            <button
              onClick={() => setShowCreate(true)}
              data-testid="create-congregation-button"
              className="flex items-center gap-2 px-4 py-2 bg-white text-gray-800 rounded-lg hover:bg-gray-100 transition-colors"
            >
              <Plus className="w-4 h-4" /> Nueva Congregación
            </button>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Summary cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6" data-testid="summary-total-balance">
            <div className="flex items-center gap-3">
              <div className="w-11 h-11 rounded-lg flex items-center justify-center" style={{ backgroundColor: '#EAF1FB' }}>
                <Wallet className="w-5 h-5" style={{ color: '#003594' }} />
              </div>
              <div>
                <p className="text-sm text-gray-500">Saldo consolidado (para dispersar)</p>
                <p className="text-2xl font-bold" style={{ color: '#051C2C' }}>Bs. {fmt(summary.total_balance)}</p>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6" data-testid="summary-total-commission">
            <div className="flex items-center gap-3">
              <div className="w-11 h-11 rounded-lg flex items-center justify-center" style={{ backgroundColor: '#F3E8FF' }}>
                <Banknote className="w-5 h-5" style={{ color: '#7C3AED' }} />
              </div>
              <div>
                <p className="text-sm text-gray-500">Comisión Caura acumulada (1%)</p>
                <p className="text-2xl font-bold" style={{ color: '#051C2C' }}>Bs. {fmt(summary.total_commission)}</p>
                <p className="text-[11px] text-gray-400">retenida en tu cuenta hasta dispersarla</p>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6" data-testid="summary-total-bank-fee">
            <div className="flex items-center gap-3">
              <div className="w-11 h-11 rounded-lg flex items-center justify-center" style={{ backgroundColor: '#FEF3C7' }}>
                <Banknote className="w-5 h-5" style={{ color: '#B45309' }} />
              </div>
              <div>
                <p className="text-sm text-gray-500">Fee banco acumulado (0,8%)</p>
                <p className="text-2xl font-bold" style={{ color: '#051C2C' }}>Bs. {fmt(summary.total_bank_fee)}</p>
                <p className="text-[11px] text-gray-400">ya lo debitó el banco de cada cobro</p>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6" data-testid="summary-total-count">
            <div className="flex items-center gap-3">
              <div className="w-11 h-11 rounded-lg flex items-center justify-center" style={{ backgroundColor: '#EAF1FB' }}>
                <Users className="w-5 h-5" style={{ color: '#003594' }} />
              </div>
              <div>
                <p className="text-sm text-gray-500">Congregaciones activas</p>
                <p className="text-2xl font-bold" style={{ color: '#051C2C' }}>{summary.total_congregations}</p>
              </div>
            </div>
          </div>
        </div>

        {/* Table */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200" data-testid="congregations-list">
          <div className="px-6 py-4 border-b border-gray-200">
            <h2 className="text-lg font-semibold" style={{ color: '#051C2C' }}>Saldo por congregación</h2>
          </div>
          {loading ? (
            <div className="p-8 text-center text-gray-500">Cargando...</div>
          ) : summary.congregations.length === 0 ? (
            <div className="p-8 text-center text-gray-500">
              <Church className="w-12 h-12 mx-auto mb-3 text-gray-300" />
              <p>Aún no hay congregaciones</p>
              <p className="text-sm mt-1">Créalas desde tu app (API de aprovisionamiento) o con el botón "Nueva Congregación".</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-gray-500 border-b border-gray-100">
                    <th className="px-6 py-3 font-medium">Congregación</th>
                    <th className="px-6 py-3 font-medium">Código</th>
                    <th className="px-6 py-3 font-medium">Formato order_reference</th>
                    <th className="px-6 py-3 font-medium text-right">Saldo (Bs.)</th>
                    <th className="px-6 py-3 font-medium text-right">Comisión Caura (Bs.)</th>
                    <th className="px-6 py-3 font-medium text-right">Fee banco (Bs.)</th>
                    <th className="px-6 py-3 font-medium">API Key</th>
                    <th className="px-6 py-3 font-medium text-right">Acciones</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {summary.congregations.map((c) => (
                    <tr key={c.project_id} className="hover:bg-gray-50 cursor-pointer" data-testid={`congregation-row-${c.code}`} onClick={() => setDetailFor(c)}>
                      <td className="px-6 py-4">
                        <div className="font-semibold" style={{ color: '#051C2C' }}>{c.name}</div>
                        <div className="text-xs text-gray-500">
                          {c.total_transactions} transacciones · {summary.bank_fee_passthrough
                            ? `${c.fee_percentage}% Caura + fee banco`
                            : `${c.fee_percentage}% comisión`}
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <span className="px-2 py-1 text-xs font-mono font-semibold rounded bg-gray-100 text-gray-800">{c.code}</span>
                      </td>
                      <td className="px-6 py-4">
                        <code className="text-xs text-gray-600">CONG-{c.code}-###</code>
                      </td>
                      <td className="px-6 py-4 text-right font-semibold" style={{ color: '#051C2C' }} data-testid={`balance-${c.code}`}>
                        {fmt(c.balance)}
                      </td>
                      <td className="px-6 py-4 text-right text-purple-700 font-medium" data-testid={`commission-${c.code}`}>
                        {fmt(c.commission_accumulated)}
                      </td>
                      <td className="px-6 py-4 text-right text-amber-700 font-medium" data-testid={`bankfee-${c.code}`}>
                        {fmt(c.bank_fee_accumulated)}
                      </td>
                      <td className="px-6 py-4">
                        {c.has_active_key ? (
                          <code className="text-xs font-mono bg-gray-100 px-2 py-1 rounded">{c.key_prefix}…</code>
                        ) : (
                          <span className="text-xs text-red-600 font-medium">Sin key activa</span>
                        )}
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex items-center justify-end gap-2">
                          <button
                            onClick={(e) => { e.stopPropagation(); setDetailFor(c); }}
                            data-testid={`view-btn-${c.code}`}
                            className="flex items-center gap-1 rounded-lg border px-2.5 py-1.5 text-xs font-medium text-white"
                            style={{ backgroundColor: '#051C2C', borderColor: '#051C2C' }}
                            title="Ver detalle de la congregación"
                          >
                            <Wallet className="w-3.5 h-3.5" /> Ver
                          </button>
                          <button
                            onClick={(e) => { e.stopPropagation(); setWithdrawFor({ ...c, bank_fee_passthrough: !!summary.bank_fee_passthrough }); }}
                            data-testid={`withdraw-btn-${c.code}`}
                            disabled={Number(c.balance || 0) <= 0}
                            className="flex items-center gap-1 rounded-lg border border-gray-300 px-2.5 py-1.5 text-xs font-medium text-gray-800 hover:bg-gray-50 disabled:opacity-40"
                            title={Number(c.balance || 0) > 0 ? 'Retirar saldo a su cuenta' : 'Sin saldo disponible'}
                          >
                            <Banknote className="w-3.5 h-3.5" /> Retirar
                          </button>
                          <button
                            onClick={(e) => { e.stopPropagation(); handleRegenerate(c.project_id, c.name); }}
                            data-testid={`regenerate-key-${c.code}`}
                            className="flex items-center gap-1 px-2.5 py-1.5 text-xs font-medium text-white rounded-lg"
                            style={{ backgroundColor: '#003594' }}
                            title="Regenerar API Key"
                          >
                            <RefreshCw className="w-3.5 h-3.5" /> Regenerar
                          </button>
                          {c.has_active_key && (
                            <button
                              onClick={(e) => { e.stopPropagation(); handleRevoke(c.project_id, c.name); }}
                              data-testid={`revoke-key-${c.code}`}
                              className="flex items-center gap-1 px-2.5 py-1.5 text-xs font-medium text-red-700 border border-red-200 rounded-lg hover:bg-red-50"
                              title="Revocar API Key"
                            >
                              <Ban className="w-3.5 h-3.5" /> Revocar
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
        <WithdrawalsHistory reloadKey={withdrawalsKey} />
      </div>

      {withdrawFor && (
        <WithdrawModal
          congregation={withdrawFor}
          onClose={() => setWithdrawFor(null)}
          onDone={() => { load(); setWithdrawalsKey((k) => k + 1); }}
        />
      )}

      {detailFor && (
        <CongregationDetailModal
          congregation={detailFor}
          onClose={() => { setDetailFor(null); load(); }}
        />
      )}

      {/* Create modal */}
      {showCreate && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" data-testid="create-congregation-modal">
          <div className="bg-white rounded-xl shadow-2xl p-6 max-w-md w-full mx-4">
            <h2 className="text-xl font-bold mb-4" style={{ color: '#051C2C' }}>Nueva Congregación</h2>
            {error && <div className="mb-3 text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg p-2">{error}</div>}
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">Nombre</label>
              <input
                type="text" value={form.name} data-testid="congregation-name-input"
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                placeholder="Ej. Congregación Bethel"
              />
            </div>
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">Código (MAYÚSCULAS/números, único)</label>
              <input
                type="text" value={form.code} data-testid="congregation-code-input"
                onChange={(e) => setForm({ ...form, code: e.target.value.toUpperCase() })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 font-mono"
                placeholder="BETHEL"
              />
              <p className="text-xs text-gray-500 mt-1">Se usará en cada orden como <code>CONG-{(form.code || 'CODIGO')}-&lt;consecutivo&gt;</code></p>
            </div>
            <div className="flex gap-3">
              <button onClick={() => { setShowCreate(false); setError(''); }} className="flex-1 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50">Cancelar</button>
              <button onClick={handleCreate} disabled={creating} data-testid="confirm-create-congregation" className="flex-1 px-4 py-2 text-white rounded-lg disabled:opacity-60" style={{ backgroundColor: '#003594' }}>
                {creating ? 'Creando...' : 'Crear'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Issued key modal (one-time) */}
      {issuedKey && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" data-testid="issued-key-modal">
          <div className="bg-white rounded-xl shadow-2xl p-6 max-w-lg w-full mx-4">
            <h2 className="text-xl font-bold text-center mb-4" style={{ color: '#051C2C' }}>API Key de la congregación</h2>
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-4 flex gap-2">
              <AlertTriangle className="w-5 h-5 text-yellow-600 flex-shrink-0" />
              <div>
                <p className="text-sm text-yellow-800 font-medium">Cópiala ahora — no se volverá a mostrar.</p>


                <p className="text-xs text-yellow-700 mt-1">Si se pierde, regenera una nueva (invalida la anterior).</p>
              </div>
            </div>
            <label className="block text-sm font-medium text-gray-700 mb-2">API Key ({issuedKey.code}):</label>
            <div className="flex gap-2 mb-4">
              <input readOnly value={issuedKey.api_key} data-testid="issued-key-value" className="flex-1 px-3 py-2 border border-gray-300 rounded-lg font-mono text-sm bg-gray-50" />
              <button onClick={() => copy(issuedKey.api_key, 'issued')} data-testid="copy-issued-key" className="px-4 py-2 text-white rounded-lg" style={{ backgroundColor: '#003594' }}>
                {copied === 'issued' ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
              </button>
            </div>
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mb-4 text-sm text-blue-900">
              Formato obligatorio de <code>order_reference</code>: <code className="font-mono">{issuedKey.format}</code>
            </div>
            <button onClick={() => setIssuedKey(null)} className="w-full px-4 py-2 bg-gray-200 text-gray-800 rounded-lg hover:bg-gray-300">Cerrar</button>
          </div>
        </div>
      )}
    </div>
  );
}

// ── Detalle de UNA congregación: saldo, movimientos y órdenes ────────
function CongregationDetailModal({ congregation, onClose }) {
  const [data, setData] = useState(null);
  const [error, setError] = useState('');

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const d = await congregationService.getActivity(congregation.project_id);
        if (alive) setData(d);
      } catch (e) {
        if (alive) setError(e.response?.data?.detail || 'No se pudo cargar el detalle');
      }
    })();
    return () => { alive = false; };
  }, [congregation.project_id]);

  const fmtDate = (ts) => (ts ? new Date(ts).toLocaleString('es-VE', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '—');
  const methodLabel = { p2p: 'Pago Móvil', immediate_debit: 'Débito Inmediato', bcv_transfer: 'Transferencia', domiciliacion: 'Domiciliación' };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" data-testid="congregation-detail-modal" onClick={onClose}>
      <div className="bg-white rounded-xl shadow-2xl max-w-3xl w-full max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="sticky top-0 bg-white border-b px-6 py-4 flex items-center justify-between">
          <div>
            <h2 className="text-lg font-bold" style={{ color: '#051C2C' }}>{congregation.name}</h2>
            <p className="text-xs text-gray-500 font-mono">{congregation.code} · {congregation.status}</p>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-700 text-2xl leading-none" data-testid="detail-close">×</button>
        </div>

        <div className="p-6 space-y-5">
          {error && <div className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg p-3" data-testid="detail-error">{error}</div>}
          {!data && !error && <p className="text-sm text-gray-400 text-center py-6" data-testid="detail-loading">Cargando movimientos…</p>}

          {data && (
            <>
              <div className="grid grid-cols-3 gap-3" data-testid="detail-totals">
                <div className="rounded-lg border border-gray-200 p-3 text-center">
                  <p className="text-[11px] text-gray-500">Saldo actual</p>
                  <p className="text-lg font-bold" style={{ color: '#003594' }} data-testid="detail-balance">Bs. {fmt(data.wallet?.balance)}</p>
                </div>
                <div className="rounded-lg border border-gray-200 p-3 text-center">
                  <p className="text-[11px] text-gray-500">Recibido total</p>
                  <p className="text-lg font-bold text-green-700">Bs. {fmt(data.wallet?.total_received)}</p>
                </div>
                <div className="rounded-lg border border-gray-200 p-3 text-center">
                  <p className="text-[11px] text-gray-500">Retirado total</p>
                  <p className="text-lg font-bold text-amber-700">Bs. {fmt(data.wallet?.total_sent)}</p>
                </div>
              </div>

              {data.bank_fee_passthrough && (
                <p className="text-[11px] text-purple-800 bg-purple-50 border border-purple-200 rounded-lg px-3 py-2" data-testid="detail-fee-note">
                  Los saldos ya vienen netos: {data.congregation.fee_percentage}% de comisión Caura + fee del banco descontados de cada cobro.
                </p>
              )}

              <p className="text-xs text-purple-800 bg-purple-50 border border-purple-200 rounded-lg px-3 py-2" data-testid="detail-commission">
                Comisión Caura acumulada de esta congregación: <strong>Bs. {fmt(data.commission_accumulated)}</strong> (retenida en la cuenta del comercio hasta su dispersión).
              </p>

              <p className="text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2" data-testid="detail-bank-fee">
                Fee del banco acumulado: <strong>Bs. {fmt(data.bank_fee_accumulated)}</strong> (ya debitado por el banco de cada cobro; no queda en la cuenta).
              </p>

              <div>
                <h3 className="text-sm font-semibold mb-2" style={{ color: '#051C2C' }}>Movimientos de la cartera</h3>
                {data.movements.length === 0 ? (
                  <p className="text-xs text-gray-400 py-3 text-center">Sin movimientos todavía</p>
                ) : (
                  <div className="border rounded-lg overflow-hidden" data-testid="detail-movements">
                    <table className="w-full text-xs">
                      <thead className="bg-gray-50 text-gray-500">
                        <tr>
                          <th className="px-3 py-2 text-left font-medium">Fecha</th>
                          <th className="px-3 py-2 text-left font-medium">Descripción</th>
                          <th className="px-3 py-2 text-right font-medium">Monto</th>
                          <th className="px-3 py-2 text-right font-medium">Saldo</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-100">
                        {data.movements.map((mv) => (
                          <tr key={mv.transaction_id}>
                            <td className="px-3 py-2 text-gray-500 whitespace-nowrap">{fmtDate(mv.created_at)}</td>
                            <td className="px-3 py-2 text-gray-700 max-w-[280px] truncate" title={mv.description}>{mv.description}</td>
                            <td className={`px-3 py-2 text-right font-semibold ${mv.type === 'payment_in' || mv.type === 'commission_in' ? 'text-green-700' : 'text-red-600'}`}>
                              {mv.type === 'payment_in' || mv.type === 'commission_in' ? '+' : '−'} {fmt(mv.amount)}
                            </td>
                            <td className="px-3 py-2 text-right text-gray-500 font-mono">{fmt(mv.balance_after)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>

              <div>
                <h3 className="text-sm font-semibold mb-2" style={{ color: '#051C2C' }}>Órdenes recientes</h3>
                {data.orders.length === 0 ? (
                  <p className="text-xs text-gray-400 py-3 text-center">Sin órdenes todavía</p>
                ) : (
                  <div className="border rounded-lg overflow-hidden" data-testid="detail-orders">
                    <table className="w-full text-xs">
                      <thead className="bg-gray-50 text-gray-500">
                        <tr>
                          <th className="px-3 py-2 text-left font-medium">Orden</th>
                          <th className="px-3 py-2 text-left font-medium">Pagador</th>
                          <th className="px-3 py-2 text-left font-medium">Método</th>
                          <th className="px-3 py-2 text-left font-medium">Estado</th>
                          <th className="px-3 py-2 text-right font-medium">Monto</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-100">
                        {data.orders.map((o) => (
                          <tr key={o.order_code}>
                            <td className="px-3 py-2 font-mono font-semibold" style={{ color: '#051C2C' }}>{o.order_code}</td>
                            <td className="px-3 py-2 text-gray-700 max-w-[180px] truncate">{o.payer_name || '—'}</td>
                            <td className="px-3 py-2 text-gray-500">{methodLabel[o.payment_method_used] || o.payment_method_used || '—'}</td>
                            <td className="px-3 py-2">
                              <span className={`px-2 py-0.5 rounded-full text-[10px] font-semibold ${o.status === 'paid' ? 'bg-green-100 text-green-800' : o.status === 'pending' ? 'bg-blue-100 text-blue-800' : 'bg-gray-100 text-gray-600'}`}>
                                {o.status}
                              </span>
                            </td>
                            <td className="px-3 py-2 text-right font-semibold">Bs. {fmt(o.paid_amount || o.amount)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
